import { createContext, useContext, useState, useEffect } from "react";
import API from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    const savedOrganization = localStorage.getItem("organization");

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      if (savedOrganization) {
        setOrganization(JSON.parse(savedOrganization));
      }

      API.get("/auth/me").then(res => {
        setUser(res.data);
        localStorage.setItem("user", JSON.stringify(res.data));

        // Store organization data if present (support both nested and flat formats)
        if (res.data.organization) {
          setOrganization(res.data.organization);
          localStorage.setItem("organization", JSON.stringify(res.data.organization));
        } else if (res.data.organization_id) {
          // Build organization object from flat fields
          const org = {
            id: res.data.organization_id,
            name: res.data.organization_name,
            slug: res.data.organization_slug,
            logo_url: res.data.organization_logo_url || null
          };
          setOrganization(org);
          localStorage.setItem("organization", JSON.stringify(org));
        }
      }).catch((err) => {
        // Backend unreachable (e.g. weekend maintenance) — keep the session;
        // only drop it when the server actually rejected the token.
        if (!err.response) return;
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("organization");
        setUser(null);
        setOrganization(null);
      });
    }
    setLoading(false);
  }, []);

  const register = async (email, name) => {
    const res = await API.post("/auth/register", { email, name });
    return res.data;
  };

  const sendOtp = async (email) => {
    // Admin login uses /admin/send-otp endpoint (requires existing admin user)
    const res = await API.post("/auth/admin/send-otp", { email });
    return res.data;
  };

  const verifyOtp = async (email, otp) => {
    const res = await API.post("/auth/verify-otp", { email, otp });

    // For admin panel login, ensure user has admin, org_admin, or superadmin role
    if (!["admin", "org_admin", "superadmin"].includes(res.data.user.role)) {
      throw new Error("Access denied. Only administrators can login here.");
    }

    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));

    // Store organization data if present (support both nested and flat formats)
    if (res.data.user.organization) {
      setOrganization(res.data.user.organization);
      localStorage.setItem("organization", JSON.stringify(res.data.user.organization));
    } else if (res.data.user.organization_id) {
      // Build organization object from flat fields
      const org = {
        id: res.data.user.organization_id,
        name: res.data.user.organization_name,
        slug: res.data.user.organization_slug,
        logo_url: res.data.user.organization_logo_url || null
      };
      setOrganization(org);
      localStorage.setItem("organization", JSON.stringify(org));
    }

    // Store session ID for activity tracking
    if (res.data.session_id) {
      localStorage.setItem("session_id", res.data.session_id);
    }

    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    // Capture organization slug before clearing localStorage
    const orgSlug = organization?.slug;

    // Clear all localStorage
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("organization");
    localStorage.removeItem("session_id");

    // Clear state
    setUser(null);
    setOrganization(null);

    // Force full page reload to clear all component state
    // This prevents stale UI elements from previous user session
    // Redirect to org-specific login if user logged in via org slug
    window.location.href = orgSlug ? `/${orgSlug}/login` : "/login";
  };

  // Helper methods for organization + campus context
  const isSuperadmin = user?.role === "superadmin";
  const isOrgAdmin = user?.role === "org_admin";
  const hasOrganization = !!organization;
  const organizationSlug = organization?.slug || null;
  const organizationName = organization?.name || null;
  const organizationLogo = organization?.logo_url || null;

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        loading,
        register,
        sendOtp,
        verifyOtp,
        logout,
        isSuperadmin,
        isOrgAdmin,
        hasOrganization,
        organizationSlug,
        organizationName,
        organizationLogo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
